// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later
//
// VIEW 8 — The record.
//
// The question: how much should I trust the number this site just showed me?
//
// Three things live here, and none is an apology. Data completeness per station and year, drawn
// in greys deliberately outside the concentration ramp so it can never be confused with a
// reading. The network's own growth, which matters because New South Wales physically deploys
// extra monitors AT fires — the network densifies precisely during the events being counted, so
// any "episodes are increasing" claim read off a growing network measures the network. And the
// five jurisdictions that are not here, each with its actual reason.

import type { Bundle } from '../data';
import { loadCoverage, loadExceedances } from '../data';
import { el, legend, panel, stat, statRow, xScroll } from '../components/ui';
import { escapeHtml, number, percent, tip, UNIT } from '../format';
import { JURISDICTION_COLOUR } from '../palette';
import { glossaryLink } from '../glossary';
import { setState } from '../state';

const GREY_RAMP = ['#2B2725', '#453D37', '#645A51', '#87796C', '#ABA093'];

function captureColour(capture: number): string {
  if (capture >= 0.95) return GREY_RAMP[4];
  if (capture >= 0.85) return GREY_RAMP[3];
  if (capture >= 0.75) return GREY_RAMP[2];
  if (capture > 0) return GREY_RAMP[1];
  return GREY_RAMP[0];
}

export async function renderRecord({ bundle, container }: { bundle: Bundle; container: HTMLElement }): Promise<void> {
  const { meta, stations } = bundle;
  const [coverage, exceedances] = await Promise.all([loadCoverage(), loadExceedances()]);

  // ── What is here ──────────────────────────────────────────────────────────
  const summary = panel({
    title: 'What this site is built from',
    subtitle:
      'Every reading here was measured by a government instrument and published under a licence that permits redistribution. Nothing is modelled, interpolated or estimated.',
  });
  summary.body.append(
    statRow(
      stat(number(meta.counts.hourlyReadings), 'hourly readings', 'after nulls and sentinel values were removed'),
      stat(number(meta.counts.activeClusters), 'station clusters', `from ${number(meta.counts.rawStations)} source stations within 2 km of each other`),
      stat(number(meta.counts.episodes), 'episodes detected', `${number(meta.counts.confirmedEpisodes)} corroborated by a second cluster`),
      stat(number(meta.counts.stationYears), 'station-years', 'with at least 75% data capture')
    )
  );
  const windows = Object.entries(meta.coverageWindow)
    .map(([jurisdiction, window]) => `<strong>${jurisdiction}</strong> ${escapeHtml(window.hourly)}`)
    .join(' · ');
  summary.body.append(
    el(
      'p',
      'note',
      `Hourly coverage: ${windows}. New South Wales also publishes daily averages back to 1994, which is what the station drill-downs draw; its hourly record is pulled from 2016 because the full hourly grid for that network is tens of gigabytes and would not fit a build. ` +
        `${glossaryLink('capture', 'Data capture')} is measured on readings, never on rows — the NSW API returns fully-formed rows with a null value for years in which the instrument did not exist, and counting those would backdate every parameter to 1994.`
    )
  );
  container.append(summary.root);

  // ── The network grew ──────────────────────────────────────────────────────
  const network = panel({
    title: 'The network is not a constant',
    subtitle:
      'How many clusters were reporting particles in each year. This matters more than it looks: New South Wales deploys extra monitors during bushfire emergencies, so the network densifies precisely during the events being counted. Any claim that episodes are becoming more frequent, read off a growing network, is measuring the network.',
  });

  const years: number[] = [];
  for (let year = Math.min(...coverage.map((row) => row.firstYear)); year <= Math.max(...coverage.map((row) => row.lastYear)); year++) {
    years.push(year);
  }
  const perYear = years.map((year) => ({
    year,
    NSW: coverage.filter((row) => row.jurisdiction === 'NSW' && row.years.some(([y]) => y === year)).length,
    QLD: coverage.filter((row) => row.jurisdiction === 'QLD' && row.years.some(([y]) => y === year)).length,
    ACT: coverage.filter((row) => row.jurisdiction === 'ACT' && row.years.some(([y]) => y === year)).length,
  }));
  const maxTotal = Math.max(...perYear.map((row) => row.NSW + row.QLD + row.ACT));

  const bars = el('div');
  for (const row of perYear) {
    const total = row.NSW + row.QLD + row.ACT;
    const line = el('div', 'bar-row');
    line.setAttribute(
      'data-tip',
      tip(`${row.year}`, `${total} clusters reporting particles`, `NSW ${row.NSW} · QLD ${row.QLD} · ACT ${row.ACT}`)
    );
    const label = el('span', 'bar-label');
    label.textContent = String(row.year);
    const track = el('span', 'bar-track');
    track.style.display = 'flex';
    for (const jurisdiction of ['NSW', 'QLD', 'ACT'] as const) {
      if (!row[jurisdiction]) continue;
      const segment = el('span', 'bar-fill');
      segment.style.width = `${(row[jurisdiction] / maxTotal) * 100}%`;
      segment.style.background = JURISDICTION_COLOUR[jurisdiction];
      segment.style.borderRadius = '0';
      track.append(segment);
    }
    const value = el('span', 'bar-value');
    value.textContent = String(total);
    line.append(label, track, value);
    bars.append(line);
  }
  network.body.append(bars);
  network.body.append(legend(['NSW', 'QLD', 'ACT'].map((j) => ({ label: j, hex: JURISDICTION_COLOUR[j] }))));
  container.append(network.root);

  // ── Completeness calendar ─────────────────────────────────────────────────
  const completeness = panel({
    title: 'How complete each station’s record is',
    subtitle:
      'One row per cluster, one cell per year, shaded by the share of that year’s hours that carry a PM2.5 reading. These greys sit deliberately outside the concentration ramp: a gap in an instrument’s record must never be able to read as clean air.',
  });

  const cellW = 15;
  const cellH = 13;
  const labelW = 150;
  const width = labelW + years.length * cellW + 8;
  const height = coverage.length * cellH + 26;

  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  node.setAttribute('viewBox', `0 0 ${width} ${height}`);
  node.setAttribute('class', 'matrix');
  node.setAttribute('role', 'img');
  node.setAttribute('aria-label', 'Data completeness by station and year');
  node.style.width = `${width}px`;
  node.style.height = `${height}px`;

  const make = (tag: string, attrs: Record<string, string | number>) => {
    const element = document.createElementNS('http://www.w3.org/2000/svg', tag);
    for (const [key, value] of Object.entries(attrs)) element.setAttribute(key, String(value));
    return element;
  };

  years.forEach((year, index) => {
    if (year % 2 !== 0) return;
    const text = make('text', { x: labelW + index * cellW + cellW / 2, y: 11, 'text-anchor': 'middle', 'font-size': 8, fill: 'var(--text-tertiary)' });
    text.textContent = String(year).slice(2);
    node.append(text);
  });

  coverage.forEach((row, index) => {
    const y = 18 + index * cellH;
    const label = make('text', { x: 0, y: y + cellH - 3, 'font-size': 9, fill: 'var(--text-secondary)' });
    label.textContent = `${row.name.length > 22 ? `${row.name.slice(0, 21)}…` : row.name} (${row.jurisdiction})`;
    node.append(label);
    const byYear = new Map(row.years);
    years.forEach((year, column) => {
      const capture = byYear.get(year) ?? 0;
      const rect = make('rect', {
        x: labelW + column * cellW,
        y,
        width: cellW - 1,
        height: cellH - 1,
        rx: 1,
        fill: captureColour(capture),
      });
      rect.setAttribute(
        'data-tip',
        capture > 0
          ? tip(`${row.name}, ${year}`, `${percent(capture, 0)} of the year’s hours carry a PM2.5 reading`, capture < 0.75 ? 'Below 75% — excluded from every station-year statistic on this site' : 'Qualifies for the station-year statistics')
          : tip(`${row.name}, ${year}`, 'This cluster reported nothing at all in this year.')
      );
      node.append(rect);
    });
  });

  completeness.body.append(xScroll(node));
  completeness.body.append(
    legend([
      { label: 'no data', hex: GREY_RAMP[0] },
      { label: 'under 75%', hex: GREY_RAMP[1], tip: 'Excluded from station-year statistics rather than estimated' },
      { label: '75–85%', hex: GREY_RAMP[2] },
      { label: '85–95%', hex: GREY_RAMP[3] },
      { label: 'over 95%', hex: GREY_RAMP[4] },
    ])
  );
  container.append(completeness.root);

  // ── Exceedances ───────────────────────────────────────────────────────────
  const compliance = panel({
    title: 'Days over the standard, as measured',
    subtitle:
      'The Australian standard allows zero exceedance days — and, in the same instrument, lets a jurisdiction set aside any day it determines was caused by a bushfire, an authorised hazard-reduction burn or a continental dust storm. Those determinations are published in annual PDF reports, not in any data feed, so nothing here has been set aside.',
  });
  const worstYears = exceedances
    .filter((row) => row.pollutant === 'pm2.5')
    .sort((a, b) => b.nepmDays - a.nepmDays)
    .slice(0, 14);
  const maxDays = worstYears[0]?.whoDays ?? 1;
  const list = el('div');
  for (const row of worstYears) {
    const line = el('button', 'bar-row');
    line.type = 'button';
    line.setAttribute(
      'data-tip',
      tip(
        `${row.name} (${row.jurisdiction}), ${row.year}`,
        `${row.nepmDays} days over the Australian standard of 25 ${UNIT}`,
        `${row.whoDays} days over the WHO 2021 guideline of 15 ${UNIT}`,
        `Annual mean ${row.annualMean} ${UNIT} — the Australian annual standard is ${row.nepmAnnual}, the WHO guideline ${row.whoAnnual}`,
        `${row.days} days of data in this year`
      )
    );
    const label = el('span', 'bar-label');
    label.textContent = `${row.name} ${row.year}`;
    const track = el('span', 'bar-track');
    track.style.display = 'flex';
    const whoFill = el('span', 'bar-fill');
    whoFill.style.width = `${((row.whoDays - row.nepmDays) / maxDays) * 100}%`;
    whoFill.style.background = 'var(--status-warn)';
    whoFill.style.borderRadius = '0';
    const nepmFill = el('span', 'bar-fill');
    nepmFill.style.width = `${(row.nepmDays / maxDays) * 100}%`;
    nepmFill.style.background = 'var(--status-bad)';
    nepmFill.style.borderRadius = '0';
    track.append(nepmFill, whoFill);
    const value = el('span', 'bar-value');
    value.textContent = `${row.nepmDays} / ${row.whoDays}`;
    line.append(label, track, value);
    line.addEventListener('click', () => setState({ station: row.clusterId, view: 'clock' }));
    list.append(line);
  }
  compliance.body.append(list);
  compliance.body.append(
    legend([
      { label: 'days over the Australian standard (25)', hex: 'var(--status-bad)' },
      { label: 'further days over the WHO guideline (15)', hex: 'var(--status-warn)' },
    ])
  );
  compliance.body.append(
    el(
      'p',
      'note',
      `Only station-years with at least 250 days of data appear here. Saying "zero exceedances" about a year missing a third of its days would read as clean air rather than as no data. ` +
        `See ${glossaryLink('exceptional event')} for why the official count is usually lower than this one.`
    )
  );
  container.append(compliance.root);

  // ── Who is missing ────────────────────────────────────────────────────────
  const missing = panel({
    title: 'The five jurisdictions that are not here',
    subtitle:
      'Melbourne, Adelaide, Perth, Hobart and Darwin are absent, for five different reasons. Two of them are about licences rather than data, which is worth knowing in its own right.',
  });
  for (const jurisdiction of meta.excluded) {
    const block = el('div');
    block.style.marginBottom = 'var(--space-lg)';
    const heading = el('h3', 'sub-head');
    heading.textContent = `${jurisdiction.name} — ${jurisdiction.reason}`;
    block.append(heading);
    block.append(el('p', 'note', escapeHtml(jurisdiction.detail)));
    if (jurisdiction.stations) {
      block.append(
        el(
          'p',
          'lorenz-caption',
          `${jurisdiction.stations} stations. Would have added: ${escapeHtml(jurisdiction.wouldAdd)}`
        )
      );
    }
    missing.body.append(block);
  }
  container.append(missing.root);

  // ── Stations excluded, and why ────────────────────────────────────────────
  if (meta.qldUnregistered?.length || meta.unlocated?.length) {
    const orphans = panel({
      title: 'Stations that measure real air but cannot be placed on a map',
      subtitle:
        'Two separate problems, both in the source rather than in the measurement. These stations are named here rather than being quietly dropped or, worse, attached to whichever neighbour looked closest.',
    });
    if (meta.unlocated?.length) {
      const block = el('div');
      block.style.marginBottom = 'var(--space-lg)';
      block.append(el('h3', 'sub-head', 'No location published'));
      block.append(
        el(
          'p',
          'note',
          `${meta.unlocated.length} stations publish a latitude and longitude of exactly zero — a sentinel meaning no location has been released, which points at a spot in the Gulf of Guinea. Their readings are real, but with no coordinate they cannot be mapped, cannot be grouped with their neighbours, and cannot take part in an episode rule that depends on distance. They are: ${meta.unlocated
            .map((name) => escapeHtml(name))
            .join(' · ')}.`
        )
      );
      orphans.body.append(block);
    }
    if (meta.qldUnregistered?.length) {
      const block = el('div');
      block.append(el('h3', 'sub-head', 'In the archive, absent from the registry'));
      block.append(
        el(
          'p',
          'note',
          `Queensland’s validated CSV archive carries columns for ${meta.qldUnregistered.length} stations its current station registry no longer contains — decommissioned sites the live endpoint has dropped. Guessing which surviving station each corresponds to would put real readings on the wrong dot, so they are excluded: ${meta.qldUnregistered
            .map((name) => escapeHtml(name))
            .join(' · ')}.`
        )
      );
      orphans.body.append(block);
    }
    container.append(orphans.root);
  }

  // ── The gates ─────────────────────────────────────────────────────────────
  const gates = panel({
    title: 'Checks that must pass before this site can build',
    subtitle:
      'Each of these exists because the corresponding mistake produces a number that is plausible, quotable and wrong. A failing check stops the build rather than shipping a green tick over a bad figure.',
  });
  const table = el('table', 'data-table');
  table.innerHTML =
    '<thead><tr><th scope="col">Check</th><th scope="col">Result</th><th scope="col">What it found</th></tr></thead><tbody></tbody>';
  const tbody = table.querySelector('tbody') as HTMLElement;
  for (const gate of meta.gates) {
    const tr = el('tr');
    const name = el('td');
    name.innerHTML = `<span class="cell-name">${escapeHtml(gate.name)}</span>`;
    const result = el('td');
    const chip = el('span', 'chip');
    chip.textContent = gate.pass ? 'pass' : gate.advisory ? 'noted' : 'fail';
    chip.style.color = gate.pass ? 'var(--status-good)' : gate.advisory ? 'var(--status-warn)' : 'var(--status-bad)';
    result.append(chip);
    const detail = el('td');
    detail.textContent = gate.detail;
    tr.append(name, result, detail);
    tbody.append(tr);
  }
  const wrap = el('div', 'table-wrap');
  wrap.append(table);
  gates.body.append(xScroll(wrap));
  container.append(gates.root);

  // ── Sources ───────────────────────────────────────────────────────────────
  const sources = panel({ title: 'Sources and licences' });
  for (const source of Object.values(meta.sources)) {
    const block = el('div');
    block.style.marginBottom = 'var(--space-md)';
    block.innerHTML =
      `<div class="sub-head"><a href="${source.url}" target="_blank" rel="noopener">${escapeHtml(source.dataset)}</a></div>` +
      `<p class="note">${escapeHtml(source.publisher)} — <a href="${source.licenceUrl}" target="_blank" rel="noopener">${escapeHtml(
        source.licence
      )}</a>. ${escapeHtml(source.attribution)}</p>`;
    sources.body.append(block);
  }
  sources.body.append(
    el(
      'p',
      'note',
      `Not to be confused with the National Pollutant Inventory, which records annual, self-reported <em>estimates of emissions</em> from industrial facilities. This site records hourly, <em>measured concentrations</em> in ambient air. They answer different questions and their numbers are not comparable. ` +
        `${number(stations.length)} station clusters are published here; a station in a registry that never reported a reading is a fact about the network rather than a place on the map, and is not drawn.`
    )
  );
  container.append(sources.root);
}
